import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  IGrabRouteRecommendationResultFailure,
  IRouteRecommendation,
} from '../../../interfaces/route-recommendations/route-recommendation';
import { IRouteAlert } from '../../../interfaces/route-recommendations/route-alert';
import { RouteAlertName } from '../../../enums/route-alert-name.enum';
import { RouteAlertStatus } from '../../../enums/route-alert-status.enum';
import { RouteRecommendationService } from '../../../services/route-recommendation.service';
import { BBWTMessageService, JobsService, UserService } from '../../../services';
import { UpdateTaskScheduleDTO } from '../../../components/task-supplier-schedule/task-supplier-schedule';
import {
  IRecommendationAdjustedTask,
  IRecommendationTask,
} from '../../../interfaces/route-recommendations/recommendation-task';
import { timeToHoursMinutesSeconds } from '../../../utils/timeToHoursMinutes';
import { splitRouteAlertDescription } from '../../../utils/route-alert-utils';
import { IRecommendationTaskToTaskSegment } from '../../../interfaces/route-recommendations/recommendation-task-to-task-segment';
import { IRouteRecommendationItinerary } from '../../../interfaces/route-recommendations/route-recommendation-itinerary';
import { IRouteRecommendationAlternateDate } from '../../../interfaces/route-recommendations/route-recommendation-alternate-date';
import { IJobDTO } from '../../../interfaces/job/job';
import { JobScreenDriverNotesJob } from '../../../interfaces/screen-specific/job-screen';
import { RoutesAnalyticsService } from '../../../services/product-analytics/routes-analytics.service';
import { ImpersonationInfo } from 'src/app/interfaces/user';

@Component({
  selector: 'planned-route-item',
  templateUrl: './planned-route-item.component.html',
  styleUrls: ['./planned-route-item.component.scss', './vertical-list-dots.scss'],
})
export class PlannedRouteItemComponent implements OnInit, AfterViewInit {
  @Output() onHideRouteClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() showDuplicateEvent: EventEmitter<IRouteRecommendation> =
    new EventEmitter<IRouteRecommendation>();
  @Output() searchByRegClicked: EventEmitter<string> = new EventEmitter<string>();

  @Input() highlightedRoute: boolean = false;
  @Input() currentOrgId: number;
  @Input() public routeItem: IRouteRecommendation;
  @Input() public isVME: boolean = false;
  @Input() public jobNumberOrVehicleRegFilter: string = null;
  protected selectedRoute: IRouteRecommendation;
  private routeToGrab: IRouteRecommendation;
  grabbedJobId: number;

  public get expandDisabled(): boolean {
    const route = this.selectedRoute ?? this.routeItem;
    return !route.hasCarryOver && route.recommendationTasks.length == 1;
  }

  readonly RouteAlertStatus = RouteAlertStatus;

  // Once a route is grabbed the date is locked in, so collapse the toggle to the chosen variant only.
  get toggleVariants(): IRouteRecommendationAlternateDate[] {
    if (this.selectedRoute?.routeGrabbed) {
      return this.routeItem.variants?.filter((x) => x.id === this.selectedRoute.id) ?? null;
    }
    return this.routeItem.variants;
  }

  get scheduleAlert(): IRouteAlert | null {
    const route = this.selectedRoute ?? this.routeItem;
    return route?.routeAlerts?.find((x) => x.name === RouteAlertName.Schedule) ?? null;
  }

  get grabBonusCount(): number {
    const route = this.selectedRoute ?? this.routeItem;
    return route?.grabBonus?.bonusCount ?? 0;
  }

  get scheduleAlertTooltip(): string {
    return splitRouteAlertDescription(this.scheduleAlert?.description ?? '').join('\n\n');
  }

  /* Planned Route vars */
  expandedView = false;

  showMapForTaskId = null;

  routeTasks: IRecommendationTask[];

  public grabbingRoute = false;

  filterParams: Params;

  chosenTaskId: number;

  taskMode: boolean = false;

  highlightedTask: number;
  showRouteItineraryPopup: boolean = false;
  showConfirmStartDatePopup: boolean = false;
  showConfirmationPopup: boolean = false;
  private itineraryPopupShownAt: number | null = null;

  constructor(
    private router: Router,
    private messageService: BBWTMessageService,
    private routeRecommendationService: RouteRecommendationService,
    private activatedRoute: ActivatedRoute,
    private routesAnalyticsService: RoutesAnalyticsService,
  ) {}

  ngAfterViewInit(): void {}

  ngOnInit(): void {
    this.routeTasks = this.routeItem.recommendationTasks.map((x) => x.task);

    this.activatedRoute.queryParams.subscribe((params) => {
      this.filterParams = params;
    });

    this.setHighlightedTask();
    this.selectedRoute = this.routeItem;
    this.routeToGrab = this.routeItem;
  }

  viewRouteDetail($event: any) {
    const routeId = this.selectedRoute?.id ?? this.routeItem.id;
    if ($event.ctrlKey || $event.metaKey) {
      // ctrl was held during click
      this.openRouteDetailInNewTab();
    } else {
      void this.router.navigate(['/app/planned-route-detail/' + routeId], {
        state: { searchFilterQueryParams: this.filterParams },
      });
    }
    this.routesAnalyticsService.sendRouteDetailViewEvent(this.selectedRoute ?? this.routeItem);
  }

  openRouteDetailInNewTab() {
    const routeId = this.selectedRoute?.id ?? this.routeItem.id;
    window.open('/app/planned-route-detail/' + routeId, '_blank');
  }

  unhideRoute() {
    this.routeRecommendationService.setHidden({
      routeRecommendationId: this.selectedRoute.id,
      hidden: false,
      routeHiddenComment: null,
      routeHiddenReasonId: null,
    });
    this.selectedRoute.hidden = false;
  }

  async watchRoute() {
    await this.routeRecommendationService.setWatched(this.selectedRoute.id, true);
    this.routesAnalyticsService.sendWatchRouteEvent(this.selectedRoute);
    this.selectedRoute.watched = true;
  }

  async unwatchRoute() {
    await this.routeRecommendationService.setWatched(this.selectedRoute.id, false);
    this.selectedRoute.watched = false;
  }

  onGrabOrDeployRouteFromPopupClick() {
    if (!this.isVME) {
      this.routesAnalyticsService.sendGrabNavigationEvent('Click grab from itinerary pop up');
    }
    this.showConfirmStartDatePopup = true;
  }

  onGrabOrDeployRouteFromConfirmStartDateClick() {
    // this.showScheduleDialog();
    this.routesAnalyticsService.sendGrabRouteEvent(this.routeItem);
    void this.grabOrDeployRouteById(this.routeToGrab.id);
    this.showConfirmStartDatePopup = false;
    this.closeItineraryPopup();
    this.showConfirmationPopup = true;
  }

  showGrabDeployConfirmDialog: boolean = false;
  grabDeployConfirmHeadline: string = '';
  grabDeployConfirmSubheadline: string = '';
  grabDeployConfirmAcceptLabel: string = '';
  private grabDeployConfirmAccept: () => void = () => {};
  private grabDeployConfirmAccepted: boolean = false;

  deployWithConfirmation() {
    this.grabDeployConfirmHeadline = 'Deploy this route?';
    this.grabDeployConfirmSubheadline = `Deploy to ${this.routeItem?.supplierDetail?.name}`;
    this.grabDeployConfirmAcceptLabel = 'Deploy Route';
    this.grabDeployConfirmAccept = () => {
      this.grabOrDeployRouteById(this.selectedRoute.id);
      this.showConfirmationPopup = true;
    };
    this.grabDeployConfirmAccepted = false;
    this.showGrabDeployConfirmDialog = true;
  }

  grabWithConfirmation() {
    this.grabDeployConfirmHeadline = 'Grab this route?';
    this.grabDeployConfirmSubheadline = '';
    this.grabDeployConfirmAcceptLabel = 'Grab Route';
    this.grabDeployConfirmAccept = () => {
      this.grabRoute();
      this.showConfirmationPopup = true;
    };
    this.grabDeployConfirmAccepted = false;
    this.showGrabDeployConfirmDialog = true;
  }

  onGrabDeployConfirmAccept() {
    this.grabDeployConfirmAccepted = true;
    this.showGrabDeployConfirmDialog = false;
    this.grabDeployConfirmAccept();
  }

  onGrabDeployConfirmCancel() {
    this.showGrabDeployConfirmDialog = false;
  }

  onGrabDeployConfirmHide() {
    if (this.grabDeployConfirmAccepted) return;
    void this.routesAnalyticsService.sendGrabRouteExitEvent(this.routeItem);
  }

  expandedViewEvent(routeItem: IRouteRecommendation) {
    this.expandedView = !this.expandedView;
  }

  convertTime(estimateTime: string) {
    return Math.round(timeToHoursMinutesSeconds(estimateTime, 'minutes'));
  }

  getTaskToTask(
    routeItem: IRouteRecommendation,
    recommendationTask: IRecommendationAdjustedTask,
  ): IRecommendationTaskToTaskSegment {
    // Find the equivalent tasktotask
    const position = recommendationTask.position;

    return routeItem.taskToTaskSegments.find((x) => x.position == position);
  }

  duplicateClicked(event, routeItem) {
    event.stopPropagation();
    this.showDuplicateEvent.emit(routeItem);
  }

  onExpandToggle() {
    if (this.expandDisabled) {
      return;
    }

    this.expandedView = !this.expandedView;
  }

  private async grabOrDeployRouteById(
    routeId: number,
    taskSupplierSchedules: UpdateTaskScheduleDTO[] = [],
  ) {
    if (this.grabbingRoute) {
      return;
    }
    this.grabbingRoute = true;
    try {
      const result = this.isVME
        ? await this.routeRecommendationService.deployRoute(routeId, taskSupplierSchedules)
        : await this.routeRecommendationService.grabRoute(routeId, taskSupplierSchedules);

      if (result.success) {
        this.messageService.ShowSuccess('Success', this.isVME ? 'Route Deployed' : 'Route Grabbed');
        this.routeItem.routeGrabbed = true;
        this.selectedRoute.routeGrabbed = true;
        this.grabbedJobId = result.newJobId;
      } else if (result.failure == IGrabRouteRecommendationResultFailure.NoLongerAvailable) {
        this.messageService.ShowError('Failure', 'Route is no longer available.');
      } else {
        this.messageService.ShowError('Failure', 'A technical error occurred');
      }
    } catch (error) {
      this.messageService.ShowError('Failure', 'A technical error occurred');
    } finally {
      this.grabbingRoute = false;
    }
  }

  private async grabRoute(taskSupplierSchedules: UpdateTaskScheduleDTO[] = []) {
    await this.grabOrDeployRouteById(this.selectedRoute.id, taskSupplierSchedules);
  }

  // Switch the card to the chosen date/carryover variant.
  async onVariantSelected(routeId: number) {
    if (routeId === this.selectedRoute.id) {
      return;
    }

    const previousId = this.selectedRoute.id;
    this.selectedRoute = await this.routeRecommendationService.get(routeId);
    this.routeToGrab = this.selectedRoute;
    this.routeTasks = this.selectedRoute.recommendationTasks.map((x) => x.task);
    this.highlightedTask = this.routeTasks[0].id;

    const variant = this.routeItem.variants?.find((x) => x.id === routeId);
    this.routesAnalyticsService.sendRouteDateSelectionEvent(
      this.routeItem,
      previousId,
      routeId,
      variant?.hasCarryOver ?? false,
      this.routeItem.variants?.length ?? 0,
    );
  }

  setHighlighted(e, id: number) {
    e.stopPropagation();

    this.highlightedTask = id;

    if (this.chosenTaskId == id) {
      this.taskMode = !this.taskMode;
      this.chosenTaskId = null; // Distinct from highlighted
    } else {
      this.chosenTaskId = id; // Distinct from highlighted
      this.taskMode = true;
    }
    this.routesAnalyticsService.sendTaskSwitchEvent(id, this.routeItem.routeNumber);
  }

  toggleMap(event: MouseEvent, taskId: number) {
    this.showMapForTaskId = this.showMapForTaskId == taskId ? null : taskId;
  }

  async onAlternateRouteSelected(routeId: number) {
    this.selectedRoute = await this.routeRecommendationService.get(routeId);
    this.routeToGrab = this.selectedRoute;
  }

  getNumberOfTasks() {
    if (this.selectedRoute.taskCount == 1) {
      return '1 Task';
    }
    return `${this.selectedRoute.taskCount} Tasks`;
  }

  async onAlternateDateSelected(routeId: number) {
    this.routeToGrab = await this.routeRecommendationService.get(routeId);
  }

  viewSupplier(id: number) {
    window.open(`/app/supplier-information/${id}`, '_blank');
  }

  showItineraryPopup() {
    this.showRouteItineraryPopup = true;
    this.itineraryPopupShownAt = Date.now();
  }

  closeItineraryPopup() {
    if (!this.showRouteItineraryPopup) {
      return;
    }

    this.showRouteItineraryPopup = false;

    const popupDurationSeconds =
      this.itineraryPopupShownAt == null
        ? 0
        : Math.max(0, Math.round((Date.now() - this.itineraryPopupShownAt) / 1000));

    this.routesAnalyticsService.sendItineraryPopupEvent(
      this.routeItem.id,
      popupDurationSeconds,
      true,
    );
    this.itineraryPopupShownAt = null;
  }

  private setHighlightedTask() {
    if (!this.jobNumberOrVehicleRegFilter) {
      this.highlightedTask = this.routeTasks[0].id;
    }

    const taskMatchingReg = this.routeTasks.find(
      (rt) =>
        rt.vehicle.registrationNumber.toUpperCase() ==
        this.jobNumberOrVehicleRegFilter.toUpperCase(),
    );
    this.highlightedTask = taskMatchingReg?.id ?? this.routeTasks[0].id;
  }
}
